import React from "react";
import { graphql, useFragment } from "react-relay";

import { faArrowUpFromBracket } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";

import { useBillingPaywall } from "~/billing/BillingIdentity";
import { getNextBillingCycleAnchorDate } from "~/billing/utils";
import { zindexRadixPopper } from "~/css/zindexes";
import { nGetText } from "~/lib/utils";
import { useCopilotPaywallsEnabled } from "~/meetingRecorder/featureFlag";
import OutOfCreditsModal from "~/stream/NoteHeader/OutOfCreditsModal";
import Menu from "~/ui/menu";
import { useModals } from "~/ui/modals";
import type { MultiSelectorOption } from "~/ui/selectors/MultiSelector";
import Tooltip from "~/ui/Tooltip";

import { useUploadModal } from "./UploadModal";

import type { UploadMenuItem_viewer$key } from "~/__generated__/UploadMenuItem_viewer.graphql";

const viewerFragment = graphql`
	fragment UploadMenuItem_viewer on Viewer {
		remainingMeetingRecordingCredits
		hasAccessToCopilot
		account {
			billingAccount {
				subscriptionBillingCycleAnchor
			}
			uploadCredits {
				includedUploadCredits
				includedCreditsUsedCurrentCycle
			}
		}
		...featureFlag_useCopilotPaywallsOn_viewer
		...BillingIdentityFeaturesPaywall_viewer
	}
`;

export default function UploadMenuItem({
	viewer: viewerKey,
	analyticsLocationDescription,
	defaultChannels = [],
}: {
	viewer: UploadMenuItem_viewer$key;
	analyticsLocationDescription: string;
	defaultChannels?: ReadonlyArray<MultiSelectorOption> | null;
}) {
	const modals = useModals();

	const viewer = useFragment(viewerFragment, viewerKey);
	const { remainingMeetingRecordingCredits, hasAccessToCopilot } = viewer;

	const paywallsEnabled = useCopilotPaywallsEnabled(viewer);
	const outOfCredits = remainingMeetingRecordingCredits === 0 && paywallsEnabled;

	const openRecordingsPaywall = React.useCallback(
		() => modals.open(({ close }) => <OutOfCreditsModal onClose={close} />),
		[modals],
	);

	const { isPaywalled: uploadsMonthlyCreditsIsPaywalled, showPaywall: showUploadsPaywall } = useBillingPaywall(
		"meeting_recording_monthly_upload_credits",
		viewer,
	);

	const {
		account: { uploadCredits, billingAccount },
	} = viewer;
	const isOutOfUploadCredits = uploadCredits.includedCreditsUsedCurrentCycle >= uploadCredits.includedUploadCredits;
	const nextCycleAnchor = React.useMemo(() => {
		return getNextBillingCycleAnchorDate(billingAccount.subscriptionBillingCycleAnchor);
	}, [billingAccount.subscriptionBillingCycleAnchor]);

	const nextCycleAnchorRelative = React.useMemo(() => {
		if (!nextCycleAnchor || uploadsMonthlyCreditsIsPaywalled || !isOutOfUploadCredits) return null;
		const now = new Date();
		const anchor = new Date(nextCycleAnchor);
		const diffTime = Math.abs(anchor.getTime() - now.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays <= 0 ? "today" : `in ${diffDays} ${nGetText(diffDays, "day")}`;
	}, [nextCycleAnchor, uploadsMonthlyCreditsIsPaywalled, isOutOfUploadCredits]);

	const uploadsCreditTooltip = nextCycleAnchorRelative
		? `Your workspace has used all uploads for this month. Upload limit will reset ${nextCycleAnchorRelative}.`
		: null;

	const openUploadModal = useUploadModal(analyticsLocationDescription);
	const openUploadModalWithNoFile = React.useCallback(() => {
		openUploadModal(null, defaultChannels);
	}, [openUploadModal, defaultChannels]);

	if (isOutOfUploadCredits) {
		if (uploadsMonthlyCreditsIsPaywalled) {
			return (
				<Menu.Item icon={faArrowAltCircleUp} iconProps={{ color: "var(--billingColor)" }} onSelect={showUploadsPaywall}>
					Upload meeting…
				</Menu.Item>
			);
		}
		return (
			<Tooltip content={uploadsCreditTooltip} zIndex={zindexRadixPopper}>
				<div>
					<Menu.Item icon={faArrowUpFromBracket} disabled>
						Upload meeting…
					</Menu.Item>
				</div>
			</Tooltip>
		);
	}
	return (
		<Tooltip
			disabled={hasAccessToCopilot}
			content="Fellow Note Taker is not enabled"
			placement="bottom"
			zIndex={zindexRadixPopper}
		>
			<Menu.Item
				icon={faArrowUpFromBracket}
				onSelect={outOfCredits ? openRecordingsPaywall : openUploadModalWithNoFile}
			>
				Upload meeting…
			</Menu.Item>
		</Tooltip>
	);
}
